import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Button from '../../shared/Button';
import ProgressBar from '../../shared/ProgressBar';
import ErrorMessage from '../../shared/ErrorMessage';
import BackendSelector from '../controls/BackendSelector';
// import ModeSelector from '../controls/ModeSelector'; // Commented out: Only Standard Mode available
import ModelSelector from '../controls/ModelSelector';
import CacheOption from '../controls/CacheOption';
import QuestionnaireDialog from './QuestionnaireDialog';
import IdeaSuggester from './IdeaSuggester';
import FeasibilityModal from './FeasibilityModal';
import JobNotification from '../../shared/JobNotification';
import { useReportGeneration } from '../../../hooks/reports/useReportGeneration';
import { useProgressTracker } from '../../../hooks/reports/useProgressTracker';

const ReportForm = ({ onReportGenerated, onJobSubmitted }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [idea, setIdea] = useState('');
  // const [advancedMode, setAdvancedMode] = useState(true); // Commented out: Advanced Mode enabled by default
  const advancedMode = true; // Fixed to advanced mode (9 sections)
  const [backend, setBackend] = useState('traditional');
  const workflowType = 'parallel'; // Force parallel for speed (no user selection)
  const [modelId, setModelId] = useState('us.meta.llama3-3-70b-instruct-v1:0');
  const [useCache, setUseCache] = useState(true);
  const [isQuestionnaireOpen, setIsQuestionnaireOpen] = useState(false);
  const [feasibilityModalOpen, setFeasibilityModalOpen] = useState(false);
  const [feasibilityData, setFeasibilityData] = useState(null);
  const [isFeasible, setIsFeasible] = useState(false); // NEW: Track if idea is feasible
  const [jobNotification, setJobNotification] = useState(null); // NEW: Job notification state
  const [pendingJobData, setPendingJobData] = useState(null); // Store job data to show after feasibility modal closes

  const { isLoading, error, feasibilityError, feasibilitySuccess, generate, clearError } = useReportGeneration();
  const { progress, complete, reset, markFeasibilityComplete } = useProgressTracker(backend, isLoading, advancedMode);

  React.useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const ideaParam = params.get('idea');
    if (ideaParam && !idea.trim()) {
      setIdea(ideaParam);
    }
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  // Watch for feasibility errors and show modal
  React.useEffect(() => {
    if (feasibilityError) {
      setFeasibilityData(feasibilityError);
      setIsFeasible(false);
      setFeasibilityModalOpen(true);
    }
  }, [feasibilityError]);

  // NEW: Watch for feasibility success and show modal + mark feasibility complete in progress
  React.useEffect(() => {
    if (feasibilitySuccess) {
      setFeasibilityData(feasibilitySuccess);
      setIsFeasible(true);
      setFeasibilityModalOpen(true);
      // Mark feasibility phase complete so progress bar moves to next phases
      markFeasibilityComplete();
    }
  }, [feasibilitySuccess, markFeasibilityComplete]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const result = await generate(backend, idea, advancedMode, workflowType, useCache, modelId);
    
    if (result) {
      complete();
      
      // NEW: Handle job submission - store job data and show after feasibility modal closes
      if (result.jobSubmitted) {
        // Store the job data to show later after feasibility modal closes
        setPendingJobData({
          message: result.message,
          jobId: result.jobId
        });
        // Notify parent (e.g. IdeaStage) so it can poll for completion
        if (onJobSubmitted) onJobSubmitted(result.jobId);
        // Don't show job notification yet - wait for feasibility modal to close
        reset();
        return;
      }
      
      // LEGACY: Handle direct report response or cache suggestions
      // Pass report, suggestions, and needsApproval flag
      onReportGenerated(result.report, result.suggestions, result.needsApproval || false);
    } else {
      reset();
    }
  };

  const handleQuestionnaireSubmit = async (formattedIdea, answers, questionnaireData) => {
    setIdea(formattedIdea);
    // setAdvancedMode(true); // Commented out: Only Standard Mode available (8Q mode uses standard analysis)
    
    // Generate report with the formatted idea AND structured questionnaire data
    // Using standard mode (advancedMode = false)
    const result = await generate(backend, formattedIdea, false, workflowType, useCache, modelId, questionnaireData);
    
    if (result) {
      complete();
      
      // NEW: Handle job submission - store job data and show after feasibility modal closes
      if (result.jobSubmitted) {
        // Store the job data to show later after feasibility modal closes
        setPendingJobData({
          message: result.message,
          jobId: result.jobId
        });
        // Notify parent (e.g. IdeaStage) so it can poll for completion
        if (onJobSubmitted) onJobSubmitted(result.jobId);
        // Don't show job notification yet - wait for feasibility modal to close
        reset();
        return;
      }
      
      // LEGACY: Handle direct report response
      // Store the original answers with the report
      const reportWithAnswers = {
        ...result.report,
        questionnaire_answers: answers,
        questionnaire_data: questionnaireData,
        report_mode: '8Q',
      };
      onReportGenerated(reportWithAnswers, result.suggestions, result.needsApproval || false);
    } else {
      reset();
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="generator-form">
        {/* 8Q Questionnaire Button */}
        <div className="questionnaire-option">
          <button
            type="button"
            onClick={() => setIsQuestionnaireOpen(true)}
            disabled={isLoading}
            className="questionnaire-btn"
          >
            📋 Use 8-Question Guided Analysis
          </button>
          <span className="questionnaire-hint">
            Answer 8 structured questions for detailed analysis
          </span>
        </div>

        <div className="questionnaire-divider">
          OR ENTER YOUR IDEA DIRECTLY
        </div>

        <div className="idea-input-section">
          <label htmlFor="idea" className="idea-label">Enter your idea:</label>
          <IdeaSuggester
            value={idea}
            onChange={setIdea}
            disabled={isLoading}
            placeholder="e.g., Radar based sleep analysis (suggestions will appear as you type)"
          />
        </div>

        {/* ModeSelector - Commented out: Only Standard Mode is available */}
        {/* <ModeSelector
          value={advancedMode}
          onChange={setAdvancedMode}
          disabled={isLoading}
        /> */}

        <BackendSelector
          value={backend}
          onChange={setBackend}
          disabled={isLoading}
        />

        <ModelSelector
          value={modelId}
          onChange={setModelId}
          disabled={isLoading}
        />

        {/* Workflow is fixed to parallel for speed - no user selection needed */}

        <CacheOption
          value={useCache}
          onChange={setUseCache}
          disabled={isLoading}
        />

        {isLoading && (
          <ProgressBar percentage={progress.percentage} step={progress.step} />
        )}

        <ErrorMessage message={error} onClose={clearError} />

        <Button 
          type="submit" 
          disabled={isLoading || !idea.trim()}
          variant="primary"
          className="generate-btn"
        >
          {isLoading ? (
            <>
              <span className="spinner"></span>
              Generating Report...
            </>
          ) : (
            'Generate Report'
          )}
        </Button>

        <div className="info-section">
          <h3>What you'll get:</h3>
          <ul>
            <li>📋 Comprehensive Technical Analysis</li>
            <li>📈 Market Trends & Competition</li>
            <li>🎯 Performance Metrics & Use Cases</li>
            <li>📄 Professional PDF Download</li>
          </ul>
        </div>
      </form>

      {/* 8Q Questionnaire Dialog */}
      <QuestionnaireDialog
        isOpen={isQuestionnaireOpen}
        onClose={() => setIsQuestionnaireOpen(false)}
        onSubmit={handleQuestionnaireSubmit}
        formSettings={{
          backend,
          modelId,
          advancedMode,
          workflowType
        }}
      />

      {/* Feasibility Modal (Success or Error) */}
      <FeasibilityModal
        isOpen={feasibilityModalOpen}
        onClose={() => {
          setFeasibilityModalOpen(false);
          clearError();
          
          // After closing feasibility modal, show job notification if there's pending job data
          if (pendingJobData) {
            setJobNotification(pendingJobData);
            setPendingJobData(null);
          }
        }}
        feasibilityData={feasibilityData}
        isFeasible={isFeasible}
      />

      {/* Job Submission Notification */}
      {jobNotification && (
        <JobNotification
          message={jobNotification.message}
          jobId={jobNotification.jobId}
          onClose={() => setJobNotification(null)}
          onViewJobs={() => {
            setJobNotification(null);
            navigate('/jobs');
          }}
        />
      )}
    </>
  );
};

export default ReportForm;
